import { Tag, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'

import { ClusterNameLink } from '@/components/common/ClusterNameLink'
import { FormattedDate } from '@/components/common/FormattedDate'
import { MonoId } from '@/components/common/MonoId'

import type { PriceEntry } from './priceUtils'
import {
  formatVmConfig,
  monthlyFromHourly,
  stripTrailingZeroes,
} from './priceUtils'

export { formatVmConfig }

function truncatePrice(v: string) {
  const num = Number(v)
  if (num >= 0.01) return `$${num.toFixed(2)}`
  if (num >= 0.001) return `$${num.toFixed(3)}`
  return `$${stripTrailingZeroes(num.toFixed(6))}`
}

function monthlyPrice(hourly: string) {
  return `$${stripTrailingZeroes(monthlyFromHourly(hourly))}`
}

function renderActiveUntil(d: string | null | undefined) {
  return d ? (
    <FormattedDate date={d} fmt="MMM d, yyyy HH:mm" />
  ) : (
    <Tooltip title="Indefinite — no expiry">
      <span style={{ fontSize: 18 }}>∞</span>
    </Tooltip>
  )
}

function renderDate(d: string) {
  return <FormattedDate date={d} fmt="MMM d, yyyy HH:mm" />
}

function priceIdColumn<T>(
  getKey: (record: T) => string,
): ColumnsType<T>[number] {
  return {
    title: 'Price ID',
    width: 170,
    render: (_: unknown, record: T) => (
      <span style={{ whiteSpace: 'nowrap' }}>
        <MonoId id={getKey(record)} />
      </span>
    ),
  }
}

export function pricePerHourColumn<T>(
  getValue: (record: T) => string,
): ColumnsType<T>[number] {
  return {
    title: 'Price/Hour',
    width: 110,
    sorter: (a: T, b: T) => Number(getValue(a)) - Number(getValue(b)),
    render: (_: unknown, record: T) => {
      const v = getValue(record)
      return <Tooltip title={`$${v}/hr`}>{truncatePrice(v)}</Tooltip>
    },
  }
}

export function pricePerMonthColumn<T>(
  getValue: (record: T) => string,
): ColumnsType<T>[number] {
  return {
    title: 'Price/Month',
    width: 110,
    sorter: (a: T, b: T) => Number(getValue(a)) - Number(getValue(b)),
    render: (_: unknown, record: T) => monthlyPrice(getValue(record)),
  }
}

function activeSinceColumn<T>(
  getValue: (record: T) => string,
): ColumnsType<T>[number] {
  return {
    title: 'Active Since',
    width: 170,
    defaultSortOrder: 'descend',
    sorter: (a: T, b: T) => getValue(a).localeCompare(getValue(b)),
    render: (_: unknown, record: T) => renderDate(getValue(record)),
  }
}

function activeUntilColumn<T>(
  getValue: (record: T) => string | null | undefined,
): ColumnsType<T>[number] {
  return {
    title: 'Active Until',
    width: 170,
    sorter: (a: T, b: T) =>
      (getValue(a) ?? '').localeCompare(getValue(b) ?? ''),
    render: (_: unknown, record: T) => renderActiveUntil(getValue(record)),
  }
}

function statusColumn<T>(
  isExpired: (record: T) => boolean,
): ColumnsType<T>[number] {
  return {
    title: 'Status',
    width: 90,
    render: (_: unknown, record: T) =>
      isExpired(record) ? <Tag>Expired</Tag> : <Tag color="green">Active</Tag>,
  }
}

export function clusterColumn<T>(
  getValue: (record: T) => string,
): ColumnsType<T>[number] {
  return {
    title: 'Cluster',
    width: 180,
    sorter: (a: T, b: T) => getValue(a).localeCompare(getValue(b)),
    render: (_: unknown, record: T) => (
      <ClusterNameLink clusterId={getValue(record)} />
    ),
  }
}

export function timelineColumns(): ColumnsType<PriceEntry> {
  return [
    priceIdColumn((r) => r.id),
    pricePerHourColumn((r) => r.pricePerHourPerQty),
    pricePerMonthColumn((r) => r.pricePerHourPerQty),
    activeSinceColumn((r) => r.activeSince),
    activeUntilColumn((r) => r.activeUntil),
    statusColumn((r) => r.expired),
  ]
}
